/**
 * WaitlistScreen.jsx
 * Waitlist management with REAL backend integration
 */
import React, { useState, useEffect } from 'react';
import { ChevronRight, Clock, Plus, X, Calendar, RefreshCw, CheckCircle, Bell, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Time preferences mapping
const TIME_PREFERENCES = [
  { id: 'any', label: 'Egal', icon: '🕐' },
  { id: 'morning', label: 'Morgens (8-12h)', icon: '🌅' },
  { id: 'midday', label: 'Mittags (12-17h)', icon: '☀️' },
  { id: 'evening', label: 'Abends (17-21h)', icon: '🌙' },
];

const DAY_OPTIONS = [
  { id: 'daily', label: 'Täglich' },
  { id: 'monday', label: 'Montag' },
  { id: 'tuesday', label: 'Dienstag' },
  { id: 'wednesday', label: 'Mittwoch' },
  { id: 'thursday', label: 'Donnerstag' },
  { id: 'friday', label: 'Freitag' },
  { id: 'saturday', label: 'Samstag' },
];

export const WaitlistScreen = ({ onBack, portalToken, services = [] }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [removing, setRemoving] = useState(null);
  
  // Form state
  const [selectedService, setSelectedService] = useState(null);
  const [timePreferences, setTimePreferences] = useState(['any']);
  const [preferredDays, setPreferredDays] = useState(['daily']);
  const [autoBook, setAutoBook] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load waitlist entries
  useEffect(() => {
    loadWaitlist();
  }, [portalToken]);

  const loadWaitlist = async () => {
    if (!portalToken) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/waitlist`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error loading waitlist:', error);
      // Don't show error toast if endpoint doesn't exist
      if (error.response?.status !== 404) {
        toast.error('Warteliste konnte nicht geladen werden');
      }
    } finally {
      setLoading(false);
    }
  };

  // Add to waitlist
  const handleAddToWaitlist = async () => {
    if (!selectedService) {
      toast.error('Bitte wählen Sie einen Service');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/portal/waitlist`,
        {
          service_id: selectedService.id,
          time_preferences: timePreferences,
          preferred_days: preferredDays,
          auto_book_enabled: autoBook,
          date_mode: 'recurring'
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      toast.success('Zur Warteliste hinzugefügt!');
      setShowAddForm(false);
      setSelectedService(null);
      setTimePreferences(['any']);
      setPreferredDays(['daily']);
      setAutoBook(false);
      loadWaitlist();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Hinzufügen';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Remove from waitlist
  const handleRemove = async (entryId) => {
    setRemoving(entryId);
    try {
      await axios.delete(`${API}/portal/waitlist/${entryId}`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      toast.success('Von Warteliste entfernt');
      loadWaitlist();
    } catch (error) {
      toast.error('Fehler beim Entfernen');
    } finally {
      setRemoving(null);
    }
  };

  // Toggle time preference
  const toggleTimePreference = (prefId) => {
    if (prefId === 'any') {
      setTimePreferences(['any']);
    } else {
      const newPrefs = timePreferences.filter(p => p !== 'any');
      if (newPrefs.includes(prefId)) {
        const filtered = newPrefs.filter(p => p !== prefId);
        setTimePreferences(filtered.length > 0 ? filtered : ['any']);
      } else {
        setTimePreferences([...newPrefs, prefId]);
      }
    }
  };

  // Toggle day preference
  const toggleDayPreference = (dayId) => {
    if (dayId === 'daily') {
      setPreferredDays(['daily']);
    } else {
      const newDays = preferredDays.filter(d => d !== 'daily');
      if (newDays.includes(dayId)) {
        const filtered = newDays.filter(d => d !== dayId);
        setPreferredDays(filtered.length > 0 ? filtered : ['daily']);
      } else {
        setPreferredDays([...newDays, dayId]);
      }
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onBack} className="w-8 h-8 bg-zinc-800 rounded-lg flex items-center justify-center">
          <ChevronRight className="w-4 h-4 text-zinc-400 rotate-180" />
        </button>
        <h1 className="text-white text-base font-bold flex-1">Warteliste</h1>
        <button 
          onClick={loadWaitlist}
          className="p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700"
        >
          <RefreshCw className={`w-4 h-4 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <p className="text-zinc-400 text-xs">
        Lass dich benachrichtigen, wenn ein Platz frei wird
      </p>

      {/* Add Form */}
      {showAddForm ? (
        <div className="bg-zinc-800/70 rounded-xl p-4 space-y-4 border border-zinc-700">
          <div className="flex justify-between items-center">
            <p className="text-white font-medium text-sm">Zur Warteliste hinzufügen</p>
            <button 
              onClick={() => setShowAddForm(false)}
              className="p-1 hover:bg-zinc-700 rounded"
            >
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          </div>

          {/* Service Selection */}
          <div className="space-y-2">
            <p className="text-zinc-400 text-xs">Service auswählen</p>
            <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => setSelectedService(service)}
                  className={`p-2 rounded-lg text-left text-xs ${
                    selectedService?.id === service.id
                      ? 'bg-yellow-500/20 border border-yellow-500 text-yellow-400'
                      : 'bg-zinc-700 text-zinc-300'
                  }`}
                >
                  {service.name}
                </button>
              ))}
            </div>
          </div>

          {/* Time Preferences */}
          <div className="space-y-2">
            <p className="text-zinc-400 text-xs">Bevorzugte Zeiten</p>
            <div className="grid grid-cols-2 gap-2">
              {TIME_PREFERENCES.map((pref) => (
                <button
                  key={pref.id}
                  onClick={() => toggleTimePreference(pref.id)}
                  className={`p-2 rounded-lg text-xs flex items-center gap-2 ${
                    timePreferences.includes(pref.id)
                      ? 'bg-yellow-500/20 border border-yellow-500 text-yellow-400'
                      : 'bg-zinc-700 text-zinc-300'
                  }`}
                >
                  <span>{pref.icon}</span>
                  <span>{pref.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Day Preferences */}
          <div className="space-y-2">
            <p className="text-zinc-400 text-xs">Bevorzugte Tage</p>
            <div className="flex flex-wrap gap-1">
              {DAY_OPTIONS.map((day) => (
                <button
                  key={day.id}
                  onClick={() => toggleDayPreference(day.id)}
                  className={`px-2 py-1 rounded text-[10px] ${
                    preferredDays.includes(day.id)
                      ? 'bg-yellow-500 text-black font-medium'
                      : 'bg-zinc-700 text-zinc-300'
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Auto-book option */}
          <label className="flex items-center gap-3 bg-zinc-700/50 p-3 rounded-lg">
            <input
              type="checkbox"
              checked={autoBook}
              onChange={(e) => setAutoBook(e.target.checked)}
              className="w-4 h-4 rounded bg-zinc-600 border-zinc-500 text-yellow-500"
            />
            <div>
              <p className="text-white text-xs">Automatisch buchen</p>
              <p className="text-zinc-500 text-[10px]">Termin automatisch buchen wenn verfügbar</p>
            </div>
          </label>

          {/* Submit Button */}
          <button
            onClick={handleAddToWaitlist}
            disabled={!selectedService || submitting}
            className="w-full py-2.5 bg-yellow-500 rounded-xl text-black text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Bell className="w-4 h-4" />
                Hinzufügen
              </>
            )}
          </button>
        </div>
      ) : (
        <>
          {/* Current Waitlist Entries */}
          <div className="space-y-2">
            <p className="text-zinc-500 text-xs">Aktive Wartelisten ({entries.length})</p>
            
            {loading ? (
              <div className="flex justify-center py-6">
                <RefreshCw className="w-6 h-6 animate-spin text-yellow-500" />
              </div>
            ) : entries.length === 0 ? (
              <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
                <Clock className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-400 text-sm">Keine aktiven Wartelisten</p>
                <p className="text-zinc-500 text-xs mt-1">
                  Füge dich zur Warteliste hinzu, um benachrichtigt zu werden
                </p>
              </div>
            ) : (
              entries.map((entry) => (
                <div key={entry.id} className="bg-zinc-800/50 rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{entry.service_name}</p>
                      <div className="flex gap-2 mt-0.5">
                        {entry.time_preferences?.map((pref, i) => (
                          <span key={i} className="text-zinc-500 text-[10px] bg-zinc-700 px-1.5 py-0.5 rounded">
                            {TIME_PREFERENCES.find(t => t.id === pref)?.label || pref}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      {entry.auto_book_enabled && (
                        <span className="text-green-400 text-[10px] flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Auto
                        </span>
                      )}
                      <p className="text-zinc-500 text-[10px]">
                        {formatDate(entry.created_at)}
                      </p>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => handleRemove(entry.id)}
                    disabled={removing === entry.id}
                    className="w-full mt-2 py-1.5 bg-red-500/20 rounded-lg text-red-400 text-xs flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    {removing === entry.id ? (
                      <RefreshCw className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <Trash2 className="w-3 h-3" />
                        Entfernen
                      </>
                    )}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Add to Waitlist Button */}
          <button 
            onClick={() => setShowAddForm(true)}
            className="w-full py-3 bg-yellow-500 rounded-xl text-black text-sm font-medium flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Zur Warteliste hinzufügen
          </button>
        </>
      )}
    </div>
  );
};

export default WaitlistScreen;
